import { useState, useMemo } from "react";
import { Button, Dropdown, Checkbox } from "antd";
import { DownOutlined } from "@ant-design/icons";
import { ColumnConfig } from "hooks/commons/useColumnSelector";
import { sortColumnsByOrder } from "common/helpers/tableUtils";

interface ColumnSelectorProps {
  tableId: string;
  availableColumns: ColumnConfig[];
  visibleColumns: string[];
  toggleColumn: (columnKey: string) => void;
  toggleAllColumns: (selected: boolean) => void;
  allSelected: boolean;
  indeterminate: boolean;
  dropdownButtonText: string;
}

// Función para agrupar columnas
const groupColumns = (columns: ColumnConfig[]) => {
  const groups: Record<string, ColumnConfig[]> = {};

  columns.forEach((column) => {
    const groupName = column.group || "";
    if (!groups[groupName]) {
      groups[groupName] = [];
    }
    groups[groupName].push(column);
  });

  return groups;
};

export function ColumnSelector({
  availableColumns,
  visibleColumns,
  toggleColumn,
  toggleAllColumns,
  allSelected,
  indeterminate,
  dropdownButtonText,
}: ColumnSelectorProps) {
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // Filtrar solo columnas seleccionables (no siempre visibles)
  const selectableColumns = useMemo(
    () => availableColumns.filter((col) => !col.alwaysVisible),
    [availableColumns]
  );

  // Agrupar columnas
  const columnGroups = useMemo(
    () => groupColumns(selectableColumns),
    [selectableColumns]
  );

  // Contenido del dropdown con grupos
  const dropdownContent = useMemo(
    () => (
      <div
        style={{
          padding: 8,
          maxHeight: 500,
          overflow: "auto",
          width: 250,
          backgroundColor: "azure",
        }}
      >
        {/* Header con "Seleccionar todas" */}
        <div
          style={{
            padding: "4px 8px",
            borderBottom: "1px solid #d9d9d9",
            marginBottom: 8,
          }}
        >
          <Checkbox
            checked={allSelected}
            indeterminate={indeterminate}
            onChange={(e) => toggleAllColumns(e.target.checked)}
          >
            <strong>Seleccionar todas</strong>
          </Checkbox>
        </div>

        {/* Grupos de columnas */}
        {Object.entries(columnGroups).map(([groupName, columns]) => {
          const sortedColumns = sortColumnsByOrder(columns);
          const allGroupSelected = sortedColumns.every((col) =>
            visibleColumns.includes(col.key)
          );
          const someGroupSelected =
            sortedColumns.some((col) => visibleColumns.includes(col.key)) &&
            !allGroupSelected;

          const toggleGroup = (selected: boolean) => {
            sortedColumns.forEach((col) => {
              if (selected && !visibleColumns.includes(col.key)) {
                toggleColumn(col.key);
              } else if (!selected && visibleColumns.includes(col.key)) {
                toggleColumn(col.key);
              }
            });
          };

          return (
            <div key={groupName} style={{ marginBottom: 12 }}>
              {/* Header del grupo */}
              <div
                style={{
                  padding: "4px 8px",
                  backgroundColor: "#f5f5f5",
                  borderRadius: 4,
                  marginBottom: 4,
                }}
              >
                <Checkbox
                  checked={allGroupSelected}
                  indeterminate={someGroupSelected}
                  onChange={(e) => toggleGroup(e.target.checked)}
                >
                  <strong>{groupName}</strong>
                </Checkbox>
              </div>

              {/* Columnas del grupo */}
              {sortedColumns.map((column) => (
                <div
                  key={column.key}
                  style={{ padding: "2px 0", marginLeft: 16 }}
                >
                  <Checkbox
                    checked={visibleColumns.includes(column.key)}
                    onChange={() => toggleColumn(column.key)}
                    style={{ width: "100%" }}
                  >
                    {column.title}
                  </Checkbox>
                </div>
              ))}
            </div>
          );
        })}
      </div>
    ),
    [
      allSelected,
      indeterminate,
      toggleAllColumns,
      columnGroups,
      visibleColumns,
      toggleColumn,
    ]
  );

  return (
    <Dropdown
      popupRender={() => dropdownContent}
      placement="bottomRight"
      open={dropdownOpen}
      onOpenChange={setDropdownOpen}
      trigger={["click"]}
    >
      <Button icon={<DownOutlined />}>{dropdownButtonText}</Button>
    </Dropdown>
  );
}
